import { useState } from 'react';
import { Plus, ArrowRight } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { faqs } from '../data/faqs';

const verticalLinesPattern = {
  backgroundImage:
    'repeating-linear-gradient(90deg, #D4512A 0, #D4512A 1px, transparent 1px, transparent 96px)',
};

function FAQItem({
  question,
  answer,
  num,
}: {
  question: string;
  answer: string;
  num: string;
}) {
  const [open, setOpen] = useState(false);

  return (
    <div className="border-t border-neutral-200 last:border-b">
      <button
        className="group flex items-start justify-between w-full py-5 text-left gap-5 transition-colors hover:text-orange-700"
        onClick={() => setOpen(!open)}
        aria-expanded={open}
      >
        <div className="flex items-baseline gap-5 flex-1">
          <span
            className={`text-caption font-bold tabular-nums tracking-widest leading-none transition-colors ${
              open ? 'text-orange' : 'text-neutral-400 group-hover:text-orange'
            }`}
          >
            {num}
          </span>
          <span className="font-semibold text-body-md text-neutral-950 leading-snug transition-colors group-hover:text-orange-700">
            {question}
          </span>
        </div>
        <motion.span
          animate={{ rotate: open ? 45 : 0 }}
          transition={{ duration: 0.2 }}
          className="flex-shrink-0 mt-0.5"
        >
          <Plus
            size={20}
            className={`transition-colors ${
              open ? 'text-orange' : 'text-neutral-400 group-hover:text-orange'
            }`}
          />
        </motion.span>
      </button>
      <AnimatePresence initial={false}>
        {open && (
          <motion.div
            key="content"
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.25, ease: 'easeInOut' }}
            className="overflow-hidden"
          >
            <p className="pl-10 pb-5 pr-10 text-body-sm text-neutral-600 leading-relaxed max-w-2xl">
              {answer}
            </p>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

export function FAQ() {
  return (
    <section id="faq" className="relative py-20 md:py-24 overflow-hidden">
      <div
        aria-hidden
        className="absolute inset-0 opacity-[0.025] pointer-events-none"
        style={verticalLinesPattern}
      />

      <div className="section-container relative">
        <div className="grid grid-cols-1 lg:grid-cols-[1fr_1.4fr] gap-10 lg:gap-20 items-start">
          {/* Esquerda — argumento */}
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: '-64px' }}
            transition={{ duration: 0.55 }}
            className="lg:sticky lg:top-24"
          >
            <div className="flex items-center gap-3 mb-6">
              <motion.span
                className="h-px bg-orange origin-left"
                initial={{ scaleX: 0 }}
                whileInView={{ scaleX: 1 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: 0.1 }}
                style={{ width: 40 }}
              />
              <span className="text-caption font-semibold uppercase tracking-[0.22em] text-neutral-700">
                FAQ
              </span>
            </div>

            <h2 className="text-display-sm md:text-display-md font-bold text-neutral-950 leading-tight tracking-tight mb-4">
              Perguntas frequentes
            </h2>

            <p className="text-body-md text-neutral-500 mb-7 max-w-sm">
              Reunimos as perguntas que gestores escolares mais nos fazem antes da primeira
              conversa.
            </p>

            <a
              href="https://wa.me/5511989082892"
              target="_blank"
              rel="noopener noreferrer"
              className="group inline-flex items-center gap-2 text-body-sm font-semibold text-orange-600 hover:text-orange-700 transition-colors"
            >
              Sua dúvida não está aqui? Fale com um especialista
              <ArrowRight size={14} className="transition-transform group-hover:translate-x-1" />
            </a>
          </motion.div>

          {/* Direita — accordion */}
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: '-48px' }}
            transition={{ duration: 0.55, delay: 0.1 }}
          >
            {faqs.map((faq, i) => (
              <FAQItem
                key={faq.question}
                num={String(i + 1).padStart(2, '0')}
                question={faq.question}
                answer={faq.answer}
              />
            ))}
          </motion.div>
        </div>
      </div>
    </section>
  );
}
